import { defineStore } from "pinia";
import { computed, ref } from "vue";

import * as cartService from "@/modules/cart/api/cart.service";
import * as itemService from "@/modules/cart/api/cartItem.service";
import * as roomService from "@/modules/cart/api/room.service";
import { getResolvedProducts } from "@/modules/catalog/api/catalog.service";

export const useCartStore = defineStore("cart", () => {
  const carts = ref([]);
  const activeCartId = ref(null);
  const rooms = ref([]);
  const items = ref([]);
  const resolvedProducts = ref([]);
  const loading = ref(false);

  const activeCart = computed(
    () => carts.value.find((c) => c.id === activeCartId.value) || null,
  );

  const enrichedRooms = computed(() => {
    return rooms.value.map((room) => {
      const roomItems = items.value
        .filter((i) => i.roomId === room.id)
        .map(enrichItem);

      return {
        ...room,
        cartItems: roomItems,
        roomTotal: roomItems.reduce((sum, i) => sum + i.lineTotal, 0),
      };
    });
  });

  const plainItems = computed(() => {
    return items.value.filter((i) => i.roomId === null).map(enrichItem);
  });

  const totalAmount = computed(() => activeCart.value?.totalAmount ?? 0);

  const totalQuantity = computed(() =>
    items.value.reduce((sum, i) => sum + Number(i.quantity || 0), 0),
  );
  async function clearCart() {
    if (!activeCartId.value) return;

    try {
      const currentItems = items.value;
      for (const item of currentItems) {
        await itemService.deleteCartItem(item.id);
      }
      await selectCart(activeCartId.value);
    } catch (error) {
      console.error("Ошибка очистки корзины:", error);
    }
  }
  function clearActiveCartState() {
    activeCartId.value = null;
    rooms.value = [];
    items.value = [];
  }

  function enrichItem(item) {
    const product = resolvedProducts.value.find((p) => p.id === item.productId);

    return {
      ...item,
      productName: product?.name ?? "Без названия",
      brandName: product?.brandName ?? "Без бренда",
      articul: item.variantArticul ?? item.VariantArticul ?? product?.articul ?? "",
      defaultImageUrl: product?.defaultImageUrl ?? null,
      unitPrice: Number(item.unitPrice || 0),
      lineTotal: Number(item.totalPrice || 0),
    };
  }

  function setCartData(cart) {
    rooms.value = cart.rooms || [];

    const roomItems = (cart.rooms || []).flatMap((r) => r.items || []);
    const plain = cart.cartItems || [];

    items.value = [...roomItems, ...plain];
  }

  async function loadUserCarts(options = {}) {
    const { throwOnError = false } = options;
    loading.value = true;
    try {
      const data = await cartService.getMyCarts();
      carts.value = Array.isArray(data) ? data : [];

      const hasActiveCart = carts.value.some(
        (c) => c.id === activeCartId.value,
      );
      if (!hasActiveCart) {
        clearActiveCartState();
      }
    } catch (e) {
      console.error(e);
      carts.value = [];
      clearActiveCartState();
      if (throwOnError) {
        throw e;
      }
    } finally {
      loading.value = false;
    }
  }

  async function selectCart(cartId) {
    if (!cartId) return;

    const cartExists = carts.value.some((c) => c.id === cartId);
    if (!cartExists) {
      clearActiveCartState();
      return;
    }

    activeCartId.value = cartId;
    loading.value = true;

    try {
      const cart = await cartService.getCartById(cartId);

      if (!cart) return;

      const index = carts.value.findIndex((c) => c.id === cart.id);
      if (index !== -1) {
        carts.value[index] = cart;
      }

      setCartData(cart);

      if (!resolvedProducts.value.length) {
        resolvedProducts.value = await getResolvedProducts();
      }
    } catch (e) {
      console.error(e);
      clearActiveCartState();
    } finally {
      loading.value = false;
    }
  }

  async function createCart(name) {
    const id = await cartService.createCart(name);
    await loadUserCarts({ throwOnError: true });
    if (Number.isInteger(id) && id > 0) {
      await selectCart(id);
    }
    return id;
  }

  async function deleteCart(cartId) {
    await cartService.deleteCart(cartId);
    await loadUserCarts();
    if (activeCartId.value === cartId) {
      clearLocalState();
    }
  }

  async function updateCartName(cartId, name) {
    await cartService.updateCart({ id: cartId, name });
    await loadUserCarts();
  }

  async function createRoom(payload) {
    await roomService.createRoom(payload);
    await selectCart(payload?.cartId ?? activeCartId.value);
  }

  async function deleteRoom(roomId) {
    await roomService.deleteRoom(roomId);
    await selectCart(activeCartId.value);
  }

  async function updateRoomName(roomId, name) {
    await roomService.updateRoom({ roomId, name });
    await selectCart(activeCartId.value);
  }

  async function addProductToCart({ productId, productVariantId = null, cartId, roomId = null, quantity = 1 }) {
    if (!cartId) {
      console.error("❌ Не выбран проект (cartId обязателен)");
      throw new Error("cartId is required");
    }

    const normalizedQuantity = Number(quantity);
    const finalQuantity = Number.isInteger(normalizedQuantity) && normalizedQuantity > 0 ? normalizedQuantity : 1;

    console.log("🔍 addProductToCart called with:", {
      productId,
      productVariantId,
      cartId,
      roomId,
      quantity: finalQuantity,
    });

    const result = await itemService.createCartItem({
      productId,
      productVariantId,
      roomId,
      cartId,
      quantity: finalQuantity,
    });

    console.log("createCartItem result:", result);

    await selectCart(cartId);
  }

  async function updateItemQuantity(item, quantity, newRoomId = undefined) {
    if (quantity <= 0) return removeItem(item.id);

    let finalRoomId = item.roomId;
    if (newRoomId !== undefined) {
      finalRoomId = newRoomId;
    }

    console.log("updateItemQuantity", {
      itemId: item.id,
      oldQuantity: item.quantity,
      newQuantity: quantity,
      oldRoomId: item.roomId,
      newRoomId: finalRoomId,
    });

    await itemService.updateCartItem(item.id, {
      quantity: quantity,
      roomId: finalRoomId,
    });

    await selectCart(activeCartId.value);
  }

  async function updateItemDiscount(itemId, selectedDiscountPercent) {
    if (!activeCartId.value) {
      throw new Error("activeCartId is required");
    }

    await itemService.updateCartItemDiscount(itemId, {
      selectedDiscountPercent,
    });

    await selectCart(activeCartId.value);
  }

  async function moveItemToRoom(itemId, targetRoomId) {
    const item = items.value.find((i) => i.id === itemId);
    if (!item) return;

    await updateItemQuantity(item, item.quantity, targetRoomId);
  }

  async function removeItem(itemId) {
    await itemService.deleteCartItem(itemId);
    await selectCart(activeCartId.value);
  }

  async function loadCartByUserId(userId) {
    void userId;
    await loadUserCarts();

    if (activeCartId.value) {
      await selectCart(activeCartId.value);
      return;
    }

    if (carts.value.length > 0) {
      await selectCart(carts.value[0].id);
    }
  }

  async function mergeGuestCartToUserCart(userId) {
    void userId;
    // Guest cart flow is not implemented in the current app.
    return false;
  }

  function clearLocalState() {
    carts.value = [];
    clearActiveCartState();
    resolvedProducts.value = [];
  }

  return {
    carts,
    activeCartId,
    rooms,
    items,
    resolvedProducts,
    loading,

    activeCart,
    enrichedRooms,
    plainItems,
    totalAmount,
    totalQuantity,

    loadUserCarts,
    selectCart,
    createCart,
    deleteCart,
    updateCartName,

    createRoom,
    deleteRoom,
    updateRoomName,

    addProductToCart,
    updateItemQuantity,
    updateItemDiscount,
    moveItemToRoom,
    removeItem,
    loadCartByUserId,
    mergeGuestCartToUserCart,

    clearLocalState,
    clearCart,
  };
});
